import clsx from "clsx";

export type SegmentedOption = {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
};

export type SegmentedControlProps = {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  size?: "sm" | "md";
  className?: string;
  fullWidth?: boolean;
  "aria-label"?: string;
};

const sizes = { sm: "h-8 text-xs px-3", md: "h-9 text-sm px-3.5" };

export function SegmentedControl({
  options,
  value,
  onChange,
  size = "md",
  className,
  fullWidth,
  ...rest
}: SegmentedControlProps) {
  return (
    <div
      role="radiogroup"
      aria-label={rest["aria-label"]}
      className={clsx(
        "inline-flex p-0.5 rounded-[var(--radius-md)] bg-[var(--color-surface-muted)] border border-[var(--color-border-subtle)]",
        fullWidth && "w-full",
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            className={clsx(
              "inline-flex items-center justify-center gap-1.5 rounded-[calc(var(--radius-md)-2px)] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1",
              selected
                ? "bg-[var(--color-surface-base)] text-[var(--color-text-primary)] shadow-sm"
                : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
              opt.disabled && "opacity-50 cursor-not-allowed",
              fullWidth && "flex-1",
              sizes[size],
            )}
          >
            {opt.icon && <span className="shrink-0">{opt.icon}</span>}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
